import Onboard from 'bnc-onboard';
import { API } from 'bnc-onboard/dist/src/interfaces';

import { getConfig } from '@config';
import { getNetworkId } from '@utils';
import { Wallet, Subscriptions, Network, RpcUrl, Theme } from '@types';
import { getAddress } from '@ethersproject/address';

const getWallets = (rpcUrl: RpcUrl) => {
  const {
    INFURA_PROJECT_ID,
    FORTMATIC_KEY,
    PORTIS_KEY,
    APP_NAME,
    APP_URL,
    APP_EMAIL,
  } = getConfig();

  const INFURA_KEY = INFURA_PROJECT_ID;
  const CONTACT_EMAIL = APP_EMAIL;
  const RPC_URL = rpcUrl;

  const wallets = [
    { walletName: 'coinbase', preferred: true },
    { walletName: 'trust', preferred: true, rpcUrl: RPC_URL },
    { walletName: 'metamask', preferred: true },
    {
      walletName: 'walletConnect',
      infuraKey: INFURA_KEY,
      preferred: true, //
    },
    { walletName: 'authereum' },
    {
      walletName: 'trezor',
      appUrl: APP_URL,
      email: CONTACT_EMAIL,
      rpcUrl: RPC_URL,
      preferred: true, //
    },
    {
      walletName: 'ledger',
      rpcUrl: RPC_URL,
      preferred: true, //
    },
    {
      walletName: 'lattice',
      rpcUrl: RPC_URL,
      appName: APP_NAME,
    },
    {
      walletName: 'keepkey',
      rpcUrl: RPC_URL,
    },
    {
      walletName: 'cobovault',
      rpcUrl: RPC_URL,
      appName: APP_NAME,
    },
    {
      walletName: 'keystone',
      rpcUrl: RPC_URL,
      appName: APP_NAME,
    },
    {
      walletName: 'fortmatic',
      apiKey: FORTMATIC_KEY,
      preferred: true,
    },
    {
      walletName: 'portis',
      apiKey: PORTIS_KEY,
      preferred: true,
      // label: 'Login with Email',
    },
    { walletName: 'opera' },
    { walletName: 'operaTouch' },
    { walletName: 'torus' },
    { walletName: 'status' },
    { walletName: 'walletLink', rpcUrl: RPC_URL, appName: APP_NAME },
    { walletName: 'imToken', rpcUrl: RPC_URL },
    { walletName: 'meetone' },
    { walletName: 'mykey', rpcUrl: RPC_URL },
    { walletName: 'huobiwallet', rpcUrl: RPC_URL },
    { walletName: 'hyperpay' },
    { walletName: 'wallet.io', rpcUrl: RPC_URL },
    { walletName: 'atoken' },
    { walletName: 'frame' },
    { walletName: 'ownbit' },
    { walletName: 'alphawallet' },
    { walletName: 'gnosis' },
    { walletName: 'xdefi' },
    { walletName: 'bitpie' },
    { walletName: 'binance' },
    { walletName: 'liquality' },
  ];

  return wallets;
};

export class BlocknativeWalletImpl implements Wallet {
  private onboard?: API;

  private getState() {
    return this.onboard?.getState();
  }

  get selectedAddress() {
    const address = this.getState()?.address;
    return address ? getAddress(address) : undefined;
  }

  get networkVersion() {
    return this.getState()?.network;
  }

  get balance() {
    return this.getState()?.balance;
  }

  get name() {
    return this.getState()?.wallet.name ?? undefined;
  }

  get provider() {
    return this.getState()?.wallet.provider;
  }

  get isCreated(): boolean {
    return !!this.onboard;
  }

  get isConnected(): Promise<boolean> {
    return this.onboard?.walletCheck() ?? Promise.resolve(false);
  }

  public create(
    ethereumNetwork: Network,
    subscriptions: Subscriptions,
    rpcUrl: RpcUrl,
    theme?: Theme
  ): boolean {
    const networkId = getNetworkId(ethereumNetwork);
    const { BLOCKNATIVE_KEY } = getConfig();

    const wallets = getWallets(rpcUrl);

    const walletCheck = [
      { checkName: 'derivationPath' },
      { checkName: 'connect' },
      { checkName: 'accounts' },
    ];

    this.onboard = Onboard({
      networkId,
      dappId: BLOCKNATIVE_KEY,
      darkMode: true,
      subscriptions,
      walletSelect: {
        wallets,
      },
      walletCheck,
    });

    return !!this.onboard;
  }

  public async connect(args?: any): Promise<boolean> {
    try {
      await this.onboard?.walletSelect(args?.name);
      const valid = await this.onboard?.walletCheck();
      return valid ?? false;
    } catch (error) {
      return false;
    }
  }

  public async changeTheme(theme: Theme) {
    const darkMode = theme !== 'light';
    if (this.onboard) {
      this.onboard.config({ darkMode });
    }
  }
}
